import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { trackInternalEvent } from '@rio/tracking'
import { CoverLetter, ExpertDoc } from 'packages/types'
import { Icon20 } from 'builder/components/Icon'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import { BriefResume, selectors, actions, BriefExpertDoc } from 'builder/modules/panel'
import ModalOverlay from 'builder/components/ModalOverlay'
import { i18n } from 'builder/utils/i18n'
import { DocumentPreview } from 'builder/components/Panel/Document/styles'
import { formatDocumentPreviewUrl } from 'builder/utils/formatDocumentPreviewUrl'
import { useMediaQueries } from 'builder/hooks/useMediaQueries'
import {
  DESKTOP_PREVIEW_WIDTH,
  PHONE_PREVIEW_WIDTH,
} from 'builder/components/Panel/Document/constants'
import { formatDate } from 'builder/utils/formatDate'
import { DocumentTypes } from 'builder/modules/constants'
import Spinner from 'builder/components/Spinner'
import CreateResumeImg from '../../assets/img/createResume.jpg'
import * as Styled from './styles'

interface IProps {
  onClose: () => void
  onCreateResume?: () => void
  submitSelectedResume: (_: BriefResume | BriefExpertDoc) => void
  isTotalJobSearchFlow?: boolean
  title?: string
  viewOnly?: DocumentTypes.resume | DocumentTypes.trDocument
  trFilterType?: DocumentTypes.resume | DocumentTypes.coverLetter
  isDocumentLoaded?: boolean
  disableDefaultCreateResumeBehavior?: boolean
  allowCreateResume?: boolean
}

const SelectResumeModal = ({
  onClose,
  onCreateResume,
  submitSelectedResume,
  isTotalJobSearchFlow,
  title,
  viewOnly,
  trFilterType = DocumentTypes.resume,
  isDocumentLoaded,
  disableDefaultCreateResumeBehavior = false,
  allowCreateResume = true,
}: IProps) => {
  const { isPhone } = useMediaQueries()
  const dispatch = useDispatch()
  const resumeList = useTypedSelector(store =>
    selectors.documentsByType(store, DocumentTypes.resume),
  )
  const trDocuments = useTypedSelector(store =>
    selectors.documentsByType(store, DocumentTypes.trDocument),
  )
  const [selectedResumeDetails, setSelectedResumeDetails] = useState<BriefResume | null>(null)

  const [loading, setLoading] = useState<'createResume' | 'selectedResume'>()

  useEffect(() => {
    trackInternalEvent('visit_app_resume_selection_popup', {
      label: 'total_job_search_solution',
    })
  }, [])

  useEffect(() => {
    if (!isDocumentLoaded) {
      dispatch(actions.fetchDocuments())
    }
  }, [dispatch, isDocumentLoaded])

  const handleClose = () => {
    trackInternalEvent('close_app_resume_selection_popup')
    onClose()
  }

  const previewUrl = useCallback(
    (document: BriefResume | CoverLetter | ExpertDoc) => {
      return formatDocumentPreviewUrl(document, {
        size: isPhone ? PHONE_PREVIEW_WIDTH : DESKTOP_PREVIEW_WIDTH,
      })
    },
    [isPhone],
  )

  const getThumbnailImg = (document: BriefResume | CoverLetter | ExpertDoc) => {
    if (document.type === DocumentTypes.trDocument && document?.thumbnail?.url) {
      return document?.thumbnail?.url
    }
    return previewUrl(document)
  }

  const getDocumentName = (document: BriefResume | CoverLetter | ExpertDoc) => {
    if (document.type === DocumentTypes.trDocument && document?.title) {
      return document?.title
    }
    return document.name || i18n.t('builder.dashboard.untitled')
  }

  const isExpertDocThumbnailPreview = (document: BriefResume | CoverLetter | ExpertDoc) => {
    if (document.type === DocumentTypes.trDocument && !document?.thumbnail?.url) {
      return true
    }
    return false
  }

  const isResumeSelected = (resume: BriefResume | CoverLetter | ExpertDoc) => {
    return selectedResumeDetails?.id === resume?.id && selectedResumeDetails?.type === resume.type
  }

  const handleResumeClick = (selectedResume: BriefResume) => {
    trackInternalEvent('select_resume_to_tailor')
    setSelectedResumeDetails(selectedResume)
  }

  const handleConfirm = () => {
    if (loading === 'selectedResume') {
      return
    }
    if (selectedResumeDetails) {
      setLoading('selectedResume')
      if (disableDefaultCreateResumeBehavior) {
        submitSelectedResume(selectedResumeDetails)
        return
      }
      submitSelectedResume(selectedResumeDetails)
      onClose()
    }
  }

  const handleCreateResumeClick = () => {
    setLoading('createResume')
    if (disableDefaultCreateResumeBehavior && onCreateResume) {
      onCreateResume()
      return
    }
    if (isTotalJobSearchFlow) {
      trackInternalEvent('click_new_resume', {
        label: 'total_job_search_solution',
      })
    }
    if (onCreateResume) {
      onCreateResume()
    }
    dispatch(
      actions.createDocument({
        type: DocumentTypes.resume,
        isResumeDistributionCreateAction: true,
      }),
    )
  }

  const renderResumeCard = (resume: BriefResume | CoverLetter | ExpertDoc, index: number) => {
    return (
      <Styled.ResumeCard
        key={`resume-${index}`}
        isSelected={isResumeSelected(resume)}
        onClick={() => handleResumeClick(resume as BriefResume)}
        data-testid="resume-list-item"
      >
        {resume.type === DocumentTypes.trDocument && (
          <Styled.TrResumeLabel>
            <Styled.TrResumeLabelIcon />
            <Styled.TrResumeLabelText>
              {i18n.t(`builder.resume_distribution.landing.modal.tr_banner`)}
            </Styled.TrResumeLabelText>
          </Styled.TrResumeLabel>
        )}
        <Styled.DocumentPreviewContainer>
          <DocumentPreview
            showUnavailablePreview={isExpertDocThumbnailPreview(resume)}
            src={getThumbnailImg(resume)}
          />
        </Styled.DocumentPreviewContainer>
        <Styled.DescriptionResumeCard>
          <Styled.NameResume>
            {getDocumentName(resume)}, {resume?.jobTitle}
          </Styled.NameResume>
          <Styled.DateResume>
            {i18n.t(`builder.resume_distribution.landing.modal.updated_at`, {
              dateString: formatDate(
                new Date(resume?.updatedAt || resume?.createdAt),
                'dd MMM, HH:mm',
              ),
            })}
          </Styled.DateResume>
        </Styled.DescriptionResumeCard>
      </Styled.ResumeCard>
    )
  }

  const renderResumes = () => {
    if (viewOnly === DocumentTypes.resume) {
      return <>{resumeList?.map((resume, index: number) => renderResumeCard(resume, index))}</>
    }
    if (viewOnly === DocumentTypes.trDocument) {
      return (
        <>
          {trDocuments
            ?.filter(item => item.doc_type === trFilterType)
            ?.map((resume, index: number) => renderResumeCard(resume, index))}
        </>
      )
    }
    return (
      <>
        {trDocuments
          ?.filter(item => item.doc_type === DocumentTypes.resume)
          ?.map((resume, index: number) => renderResumeCard(resume, index))}
        {resumeList?.map((resume, index: number) => renderResumeCard(resume, index))}
      </>
    )
  }

  return (
    <>
      <ModalOverlay
        onClick={handleClose}
        backgroundOpacity={0.6}
        overlayFadeDuration={350}
        contentSlideDuration={350}
      >
        <Styled.Container data-testid="select-resume-modal">
          <Styled.Close onClick={handleClose} data-testid="select-resume-modal-close">
            <Icon20.Close />
          </Styled.Close>
          <Styled.Title>
            {title || i18n.t(`builder.resume_distribution.landing.modal.title`)}
          </Styled.Title>
          <Styled.ResumeList>
            {renderResumes()}
            {allowCreateResume && (
              <Styled.ResumeCard
                key="resume-create"
                isSelected={false}
                onClick={loading ? undefined : handleCreateResumeClick}
              >
                {loading === 'createResume' ? (
                  <Styled.SpinnerContainer>
                    <Spinner />
                  </Styled.SpinnerContainer>
                ) : (
                  <Styled.DocumentPreviewContainer>
                    <Styled.CreateResumeImage alt="create resume" src={CreateResumeImg} />
                  </Styled.DocumentPreviewContainer>
                )}
                <Styled.DescriptionResumeCard>
                  <Styled.NameResume>
                    {i18n.t(`builder.resume_distribution.landing.modal.create_resume`)}
                  </Styled.NameResume>
                </Styled.DescriptionResumeCard>
              </Styled.ResumeCard>
            )}
          </Styled.ResumeList>
          <Styled.Footer>
            <Styled.CloseButton onClick={handleClose} data-testid="select-resume-modal-cancel">
              {i18n.t(`builder.resume_distribution.landing.modal.cancel`)}
            </Styled.CloseButton>
            <Styled.ConfirmButton
              isLoading={loading === 'selectedResume'}
              onClick={handleConfirm}
              data-testid="select-resume-modal-confirm"
            >
              {i18n.t(`builder.resume_distribution.landing.modal.confirm`)}
            </Styled.ConfirmButton>
          </Styled.Footer>
        </Styled.Container>
      </ModalOverlay>
    </>
  )
}

export default SelectResumeModal
